import { Button, Form, Input, message } from 'antd';
import React, { useEffect, useState } from 'react';

import TurnstilePortal from '@/components/common/TurnstilePortal';
import VerificationError from '@/components/common/VerificationError';
import { useTranslate } from '@/hooks/useTranslate';
import { useTurnstileVerification } from '@/hooks/useTurnstileVerification';
import {
  checkLogin,
  emailLogin,
  getEmailCode,
  getUserInfo,
  loginOrInsert,
  updatePassword,
} from '@/services/login';
import useLoginStore from '@/store/Login';
import UserStore from '@/store/User';
import {
  ciphertext,
  getAgreementUrl,
  getCookie,
  isValidEmail,
  passwordReg,
  setCookie,
} from '@/utils';
import { EventNewID, GoogleEventID, TLoginInType } from '@/utils/eventlog/eventNewConfig';
import {
  formatSignUpDate,
  getSubscriptionPlan,
  getSubscriptionStatus,
  pushGTMEvent,
} from '@/utils/eventlog/gtmDataLayer';
import { trackEvent, trackGoogleEvent, userLogin } from '@/utils/eventlog/logAdapter';
import { getFingerprint } from '@/utils/fingerprint';

import CloseIcon from '../icon/close';
import { AppleLogin } from './AppleLogin';
import { GoogleLogin } from './GoogleLogin';
import styles from './style.module.scss';

export interface LoginModalProps {
  onClose?: () => void;
  onSuccess?: () => void;
}

type FieldType = {
  email?: string;
  password?: string;
  code?: string;
  remember?: boolean;
};

// UI 状态枚举
enum LoginUIState {
  INPUT_EMAIL = 1, // 输入邮箱
  INPUT_PASSWORD = 2, // 输入密码
  INPUT_CODE = 3, // 输入验证码
  SETUP_PASSWORD = 4, // 设置密码
}

export const LoginContent: React.FC<LoginModalProps> = ({ onClose, onSuccess }) => {
  const { t } = useTranslate();
  const [form] = Form.useForm();
  const [codeForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [setupPasswordForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [uiState, setUIState] = useState<LoginUIState>(LoginUIState.INPUT_EMAIL);

  const [accountId, setAccountId] = useState('');
  const [salt, setSalt] = useState<string>('');
  const [isNeedSetupPassword, setIsNeedSetupPassword] = useState(false);

  // 验证码倒计时
  const [sendCodeState, setSendCodeState] = useState(true);
  const [countDown, setCountDown] = useState(60);
  const [timer, setTimer] = useState<NodeJS.Timeout>();

  // 错误信息
  const [emailError, setEmailError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Turnstile人机验证Hook
  const {
    turnstileToken,
    isVerifying,
    showManagedTurnstile,
    showInvisibleTurnstile,
    invisibleTurnstileRef,
    startVerification,
    resetVerification,
    handleInvisibleSuccess,
    handleInvisibleError,
    handleManagedSuccess,
    handleManagedError,
  } = useTurnstileVerification({
    onSuccess: (token) => {
      // 验证完成后,继续检查账户
      const email = form.getFieldValue('email');
      if (email) {
        checkAccount(email);
      }
    },
    onError: (error) => {
      console.error('Turnstile verification error:', error);
      setLoading(false);
    },
  });

  // 加载保存的邮箱
  useEffect(() => {
    const savedEmail = localStorage.getItem('remembered_email');
    const rememberMe = localStorage.getItem('remember_me') === 'true';

    if (savedEmail && rememberMe) {
      form.setFieldsValue({
        email: savedEmail,
        remember: true,
      });
      setAccountId(savedEmail);
    }
  }, [form]);

  // 清理倒计时
  useEffect(() => {
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [timer]);

  // 启动倒计时
  const startCountDown = () => {
    setSendCodeState(true);
    setCountDown(59);

    const startTimer = () => {
      setCountDown((prev) => {
        if (prev <= 1) {
          setSendCodeState(false);
          return 60;
        }
        const newTimer = setTimeout(startTimer, 1000);
        setTimer(newTimer);
        return prev - 1;
      });
    };

    const newTimer = setTimeout(startTimer, 1000);
    setTimer(newTimer);
  };

  // 发送邮箱验证码
  const sendCode = async () => {
    if (!accountId) {
      return message.error(t('merror.login.email'));
    }

    if (!isValidEmail(accountId)) {
      return message.error(t('merror.login.email.error'));
    }

    try {
      const res = await getEmailCode(accountId);
      if (res.code === 200 || res.code === 10103011) {
        startCountDown();
        if (res.code === 10103011) {
          message.info(t('merror.login.input.send.code'));
        }
      } else {
        message.error('Failed to send code');
        trackEvent(EventNewID.SEND_EMAIL_CODE_FAILED, { data: res });
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Network error';
      setSendCodeState(false);
      message.error(errorMsg);
      trackEvent(EventNewID.SEND_EMAIL_CODE_FAILED, { data: errorMsg });
    }
  };

  // 检查账号状态
  const checkAccount = async (email: string) => {
    try {
      setLoading(true);

      // 同时检查密码登录和验证码登录
      const [checkResPassword, checkResEmailCode] = await Promise.all([
        checkLogin({ accountType: 65, accountId: email }),
        checkLogin({ accountType: 60, accountId: email }),
      ]);

      let newUIState = LoginUIState.INPUT_CODE;
      let hasPassword = false;

      // 检查密码登录状态
      if (checkResPassword.data?.state === 3 && checkResPassword.data?.salt) {
        // 已注册且有密码
        hasPassword = true;
        setSalt(checkResPassword.data.salt);
        newUIState = LoginUIState.INPUT_PASSWORD;
      }

      // 检查验证码登录状态
      if (checkResEmailCode.data?.state === 1) {
        useLoginStore.getState().setIsNewUser(true);
        // 未注册，需要注册
        setIsNeedSetupPassword(true);
        newUIState = LoginUIState.INPUT_CODE;
      } else if (checkResEmailCode.data?.state === 3 && !hasPassword) {
        // 已注册但无密码
        newUIState = LoginUIState.INPUT_CODE;
      }

      setUIState(newUIState);

      // 如果是验证码登录，自动发送验证码
      if (newUIState === LoginUIState.INPUT_CODE) {
        await sendCode();
      }

      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setSendCodeState(false);
      message.error(error.message || 'Check account failed');
    }
  };

  // 邮箱输入提交
  const onEmailFinish = async (values: FieldType) => {
    const email = values.email?.trim() || '';
    if (!email) {
      setEmailError(t('merror.login.email'));
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError(t('merror.login.email.error'));
      return;
    }

    setAccountId(email);

    // 如果还没有验证或正在验证中,先启动验证流程
    if (!turnstileToken && !isVerifying) {
      setLoading(true);
      startVerification();
      return;
    }

    // 已验证,继续检查账户
    await checkAccount(email);
  };

  // 验证码登录
  const onCodeFinish = async (values: FieldType) => {
    if (!values.code) {
      setCodeError(t('merror.login.input.code'));
      return;
    }

    try {
      setLoading(true);

      const fingerprint = await getFingerprint();
      const loginRes = await emailLogin({
        accountId,
        code: values.code,
        fingerprint,
      });

      if (loginRes?.code !== 200) {
        setLoading(false);
        let errorNote = '';
        if (loginRes.data?.code === 10103004 || loginRes.code === 10103004) {
          errorNote = 'Invalid verification code';
          setCodeError(t('merror.login.code.error') || errorNote);
        } else if (loginRes.code === 12301410) {
          errorNote = 'Exceeds the limit';
          setCodeError(t('Errors-exceeds-the-Limit-please-try-again-5-minute'));
        } else {
          errorNote = loginRes.message || 'Login failed';
          setCodeError(errorNote);
        }
        pushGTMEvent('login_fail', { method: 'email', error_note: errorNote });
        return;
      }
      setCookie(
        'user',
        {
          ...loginRes.data,
          accountId,
          accountType: 60,
        },
        30,
      );
      if (loginRes.data?.uid) {
        setCookie('falcocut_user_id', loginRes.data.uid, 30);
      }

      setLoading(false);

      // 如果需要设置密码
      if (isNeedSetupPassword) {
        pushGTMEvent('email_sign_up_code_validated', { method: 'email' });
        setUIState(LoginUIState.SETUP_PASSWORD);
        trackGoogleEvent(GoogleEventID.SignUp, {
          user_id: loginRes.data.uid,
          is_free_user: true,
        });
        return;
      }

      // 登录成功
      await handleLoginSuccess();
      // 登录成功后重置验证状态
      resetVerification();
    } catch (error: any) {
      setLoading(false);
      message.error(error.message);
      pushGTMEvent('login_fail', {
        method: 'email',
        error_note: error.message || 'unknown',
      });
      // 登录失败,重置验证状态
      resetVerification();
    }
  };

  // 密码登录
  const onPasswordFinish = async (values: FieldType) => {
    if (!values.password) {
      setPasswordError(t('merror.login.inputPsd'));
      return;
    }

    try {
      setLoading(true);

      // 加密密码
      const {
        salt: newSalt,
        oldPassword,
        newPassword,
      } = await ciphertext(salt, values.password);

      const fingerprint = await getFingerprint();
      const loginRes = await loginOrInsert({
        accountId,
        accountType: 65,
        salt: newSalt,
        newPassword,
        oldPassword,
        isResult: false,
        fingerprint,
      });

      if (loginRes.code !== 200) {
        setLoading(false);
        let errorNote = '';
        if (loginRes.data?.code === 10103013 || loginRes.code === 10103013) {
          errorNote = 'Password error';
          setPasswordError(t('merror.login.password.error'));
        } else {
          errorNote = loginRes.message || 'Login failed';
          setPasswordError(errorNote);
        }
        pushGTMEvent('login_fail', { method: 'email', error_note: errorNote });
        return;
      }

      // 保存登录信息
      setCookie(
        'user',
        {
          ...loginRes.data,
          accountId,
          accountType: 65,
        },
        30,
      );
      if (loginRes.data?.uid) {
        setCookie('falcocut_user_id', loginRes.data.uid, 30);
      }
      setLoading(false);

      // 登录成功
      await handleLoginSuccess('Email');
    } catch (error: any) {
      setLoading(false);
      message.error(error.message);
      pushGTMEvent('login_fail', {
        method: 'email',
        error_note: error.message || 'unknown',
      });
    }
  };

  // 设置密码
  const onSetupPasswordFinish = async (values: FieldType) => {
    if (!values.password) {
      setPasswordError(t('merror.login.inputPsd'));
      return;
    }

    if (!passwordReg(values.password)) {
      setPasswordError(t('merror.login.password.desc'));
      return;
    }

    try {
      setLoading(true);

      const { salt: newSalt, newPassword } = await ciphertext('', values.password);

      const res = await updatePassword({
        accountId,
        salt: newSalt,
        newPassword,
      });

      setLoading(false);

      if (res.code !== 200) {
        setPasswordError(res.message || 'Setup password failed');
        return;
      }

      // 密码设置成功，直接登录
      // message.success(t('Set-Password-Successful'));
      await handleLoginSuccess('Email');
    } catch (error: any) {
      setLoading(false);
      message.error(error.message);
    }
  };

  // Google 登录成功
  const handleGoogleSuccess = async () => {
    try {
      // 登录成功
      await handleLoginSuccess('Google');
    } catch (error: any) {
      setLoading(false);
      message.error(error.message || 'Google login failed');
    }
  };

  // Apple 登录成功
  const handleAppleSuccess = async () => {
    try {
      await handleLoginSuccess('Apple');
    } catch (error: any) {
      setLoading(false);
      message.error(error.message || 'Apple login failed');
    }
  };

  // 统一的登录成功处理
  const handleLoginSuccess = async (loginType: TLoginInType = 'Email') => {
    message.success(t('merror.login.message.sucess'));
    await userLogin({ type: loginType });

    // 刷新 currentVip 后上报 GTM login 事件
    await UserStore.getState().getUserBenefit(false);
    const uid = getCookie('user')?.uid || '';
    pushGTMEvent('login', {
      method: loginType.toLowerCase() as 'google' | 'apple' | 'email',
      user_id: uid,
      subscription_plan: getSubscriptionPlan(),
      subscription_status: getSubscriptionStatus(),
    });

    // 新用户注册成功时上报 GTM sign_up 事件
    if (useLoginStore.getState().isNewUser) {
      pushGTMEvent('sign_up', {
        method: loginType.toLowerCase() as 'google' | 'apple' | 'email',
        sign_up_date: formatSignUpDate(),
        user_id: uid,
        subscription_plan: 'Free',
        subscription_status: 'free',
      });
    }

    onSuccess?.();
  };

  // 忘记密码
  const handleForgotPassword = () => {
    setIsNeedSetupPassword(true);
    setUIState(LoginUIState.INPUT_CODE);
    sendCode();
  };

  // 切换到验证码登录
  const switchToCode = () => {
    setUIState(LoginUIState.INPUT_CODE);
    sendCode();
  };

  // 切换到密码登录
  const switchToPassword = () => {
    setUIState(LoginUIState.INPUT_PASSWORD);
  };

  // 切换账号
  const switchAccount = () => {
    setUIState(LoginUIState.INPUT_EMAIL);
    setAccountId('');
    setEmailError('');
    setCodeError('');
    setPasswordError('');
    setSalt('');
    setIsNeedSetupPassword(false);
    if (timer) {
      clearTimeout(timer);
    }
    setSendCodeState(false);
    setCountDown(60);
    form.resetFields();
    codeForm.resetFields();
    passwordForm.resetFields();
    setupPasswordForm.resetFields();
    // 重置人机验证状态
    resetVerification();
  };

  // 跳过设置密码
  const skipSetupPassword = async () => {
    await handleLoginSuccess('Email');
  };

  // 渲染输入邮箱UI
  const renderEmailInput = () => {
    return (
      <div>
        <h2 className={styles.title}>
          <span>{t('merror.login.start')}</span>
          <span
            className="absolute right-[0px] top-[0px] cursor-pointer"
            onClick={onClose}
          >
            <CloseIcon />
          </span>
        </h2>

        {/* 验证提示 - 当显示交互式验证组件时展示，放在最顶部 */}
        {showManagedTurnstile && (
          <div className="mt-6">
            <VerificationError />
          </div>
        )}

        <div className="h-6"></div>
        <div className={styles.oauthButtons}>
          <GoogleLogin
            disabled={loading}
            onSuccess={handleGoogleSuccess}
            onError={(error) => {
              trackEvent(EventNewID.GOOGLE_LOGIN_FAILED, { loginResult: error });
              pushGTMEvent('login_fail', {
                method: 'google',
                error_note:
                  error?.message ||
                  (typeof error === 'string'
                    ? error
                    : JSON.stringify(error) || 'unknown'),
              });
            }}
          />
          <AppleLogin
            disabled={loading}
            onSuccess={handleAppleSuccess}
            onError={(error) => {
              trackEvent(EventNewID.APPLE_LOGIN_FAILED, { loginResult: error });
              pushGTMEvent('login_fail', {
                method: 'apple',
                error_note:
                  error?.message ||
                  (typeof error === 'string'
                    ? error
                    : JSON.stringify(error) || 'unknown'),
              });
            }}
          />
        </div>

        <div className={styles.divider}>
          <div className={styles.line} />
          <span className={styles.text}> {t('merror.login.email.or')}</span>
          <div className={styles.line} />
        </div>

        <div className="pt-[24px]">
          {/* <p className="mb-2 text-sm font-medium">
            {t('merror.login.email.input.title')}
          </p> */}
          <Form
            form={form}
            name="emailForm"
            initialValues={{ remember: false }}
            onFinish={onEmailFinish}
            autoComplete="off"
          >
            <Form.Item
              name="email"
              validateStatus={emailError ? 'error' : ''}
              help={emailError}
            >
              <Input
                placeholder={t('merror.login.email')}
                className={styles.input}
                disabled={loading || isVerifying}
                onChange={(e) => {
                  setAccountId(e.target.value);
                  setEmailError('');
                }}
              />
            </Form.Item>

            <Form.Item className={styles.submitItem}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading || isVerifying}
                className={styles.submitButton}
                element-name="email_btn_login_popup"
              >
                {isVerifying
                  ? 'Verifying...'
                  : t('merror.login.input.email.next') || 'Next'}
              </Button>
            </Form.Item>
          </Form>

          {/* Turnstile验证组件(通过Portal渲染) - 放在提交按钮和协议之间 */}
          <div data-turnstile-container className="w-full py-2">
            <TurnstilePortal
              showInvisible={showInvisibleTurnstile}
              showManaged={showManagedTurnstile}
              invisibleRef={invisibleTurnstileRef}
              onInvisibleSuccess={handleInvisibleSuccess}
              onInvisibleError={handleInvisibleError}
              onManagedSuccess={handleManagedSuccess}
              onManagedError={handleManagedError}
            />
          </div>

          <div className="flex justify-center items-center flex-wrap pt-3 text-xs text-gray-500">
            <span>{t('merror.login.oversea.protocol.agree')} </span>
            <a
              className={styles.link}
              style={{ fontSize: '12px', marginLeft: '4px', color: '#636EF4' }}
              href={getAgreementUrl().user}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('merror.login.use')}
            </a>
            ，
            <a
              className={styles.link}
              style={{ fontSize: '12px', color: '#636EF4' }}
              href={getAgreementUrl().privicy}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('merror.login.privacy')}
            </a>
            <span style={{ margin: '0 4px' }}>
              {t('merror.login.protocol.and') || 'and'}
            </span>
            <a
              className={styles.link}
              style={{ fontSize: '12px', color: '#636EF4' }}
              href={getAgreementUrl().member}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('Membership Policy')}
            </a>
          </div>
        </div>
      </div>
    );
  };

  // 渲染验证码输入UI
  const renderCodeInput = () => {
    return (
      <div className="py-1">
        <h2 className={styles.title}>
          <span>{t('merror.login.input.code') || 'Enter verification code'}</span>
          <span
            className="absolute right-[0px] top-[0px] cursor-pointer"
            onClick={onClose}
          >
            <CloseIcon />
          </span>
        </h2>

        <p className="text-sm text-gray-600 mt-4">
          {t('merror.login.input.send.code')}
          <strong>{accountId}</strong>
        </p>

        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm font-medium">{t('merror.login.code.title')}</p>
            {sendCodeState ? (
              <p className="text-sm" style={{ opacity: 0.5, color: '#636EF4' }}>
                {t('merror.login.code.resend')} ({countDown}s)
              </p>
            ) : (
              <button
                className="text-sm cursor-pointer"
                style={{ color: '#636EF4' }}
                onClick={sendCode}
                element-name="email_sign_up_resend_code"
              >
                {t('merror.login.code.resend')}
              </button>
            )}
          </div>

          <Form form={codeForm} onFinish={onCodeFinish} autoComplete="off">
            <Form.Item
              name="code"
              validateStatus={codeError ? 'error' : ''}
              help={codeError}
            >
              <Input
                placeholder={t('merror.login.input.code.placehoder')}
                className={styles.input}
                disabled={loading}
                onChange={() => setCodeError('')}
              />
            </Form.Item>

            {salt && (
              <div className="pt-2 pb-4">
                <button
                  className="text-sm cursor-pointer"
                  style={{ color: '#636EF4' }}
                  onClick={switchToPassword}
                  type="button"
                >
                  {t('merror.login.password.switch.password')}
                </button>
              </div>
            )}

            <Form.Item className={styles.submitItem}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className={styles.submitButton}
              >
                {t('merror.login.input.code.next')}
              </Button>
            </Form.Item>
          </Form>

          <div className="pt-3 w-full flex justify-center">
            <button
              className="text-sm cursor-pointer"
              style={{ color: '#636EF4' }}
              onClick={switchAccount}
            >
              {t('merror.login.another.account')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 渲染密码输入UI
  const renderPasswordInput = () => {
    return (
      <div className="py-1">
        <h2 className={styles.title}>
          <span>{t('merror.login.password.input.title2')}</span>
          <span
            className="absolute right-[0px] top-[0px] cursor-pointer"
            onClick={onClose}
          >
            <CloseIcon />
          </span>
        </h2>

        <p className="text-sm text-gray-600 mt-4">
          <strong>{accountId}</strong>
        </p>

        <div className="mt-6">
          {/* <p className="text-sm font-medium mb-2">
            {t('merror.login.password.input.title') || 'Password'}
          </p> */}

          <Form form={passwordForm} onFinish={onPasswordFinish} autoComplete="off">
            <Form.Item
              name="password"
              validateStatus={passwordError ? 'error' : ''}
              help={passwordError}
            >
              <Input.Password
                placeholder={t('merror.login.password.placeholder')}
                className={styles.input}
                disabled={loading}
                onChange={() => setPasswordError('')}
              />
            </Form.Item>

            <div className="flex flex-col gap-3 pt-2 pb-2">
              <button
                className="text-sm cursor-pointer text-left"
                style={{ color: '#636EF4' }}
                onClick={handleForgotPassword}
                type="button"
              >
                {t('merror.login.password.forget')}
              </button>
            </div>

            <Form.Item className={styles.submitItem}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className={styles.submitButton}
                element-name="email_btn_login_popup"
              >
                {t('merror.login.next')}
              </Button>
            </Form.Item>
          </Form>

          <div className="pt-3 w-full flex justify-center">
            <button
              className="text-sm cursor-pointer"
              style={{ color: '#636EF4' }}
              onClick={switchAccount}
            >
              {t('merror.login.another.account')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 渲染设置密码UI
  const renderSetupPassword = () => {
    return (
      <div className="py-1">
        <h2 className={styles.title}>
          <span>{t('merror.login.password.title')}</span>
          <span
            className="absolute right-[0px] top-[0px] cursor-pointer"
            onClick={onClose}
          >
            <CloseIcon />
          </span>
        </h2>

        <p className="text-sm text-gray-600 mt-4">{t('merror.login.password.desc')}</p>

        <div className="mt-6">
          <p className="text-sm font-medium mb-2">
            {t('merror.login.password.input.title')}
          </p>

          <Form
            form={setupPasswordForm}
            onFinish={onSetupPasswordFinish}
            autoComplete="off"
          >
            <Form.Item
              name="password"
              validateStatus={passwordError ? 'error' : ''}
              help={passwordError}
            >
              <Input.Password
                placeholder={t('merror.login.password.placeholder')}
                className={styles.input}
                disabled={loading}
                onChange={() => setPasswordError('')}
              />
            </Form.Item>

            <Form.Item className={styles.submitItem}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className={styles.submitButton}
              >
                {t('merror.login.next')}
              </Button>
            </Form.Item>
          </Form>

          <div className="pt-3 w-full flex justify-center">
            <button
              className="text-sm cursor-pointer"
              style={{ color: '#636EF4' }}
              onClick={skipSetupPassword}
            >
              {t('merror.login.password.ignore')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.loginContent}>
      {uiState === LoginUIState.INPUT_EMAIL && renderEmailInput()}
      {uiState === LoginUIState.INPUT_CODE && renderCodeInput()}
      {uiState === LoginUIState.INPUT_PASSWORD && renderPasswordInput()}
      {uiState === LoginUIState.SETUP_PASSWORD && renderSetupPassword()}
    </div>
  );
};
